/* eslint-disable consistent-return */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import moment from 'moment';
import {
  NavigationMenu,
  AppBarSearch,
  Pagination,
  EmptyState,
  ErrorState,
  SearchNotFound
} from '@plataforma/components';
import { useRemoteConfig } from '_/context/RemoteConfigContext';
import { USER_TYPE } from '@plataforma/utils';
import { getUserProfile } from '@services/auth';
import ModalOption from '../ModalOption';
import Carousel from '../Carousel';
import Announcement from '../Announcement';

import * as S from './styles';

function ProcessesWrapper({
  children,
  search = {},
  pagination = {},
  showEmptyState,
  showErrorState,
  showEmptySearch,
  banners,
  hasBonusAvaliable,
  emptyStateAction,
  errorStateAction,
  menuItems,
  callbackMenu,
  announcement,
  modalStatus,
  modalType,
  setFilter,
  selectStatusOptions,
  selectedStatus,
  setSelectedStatus
}) {
  const { t } = useTranslation();
  const [remoteState] = useRemoteConfig();
  const filteredBanners = useMemo(() => {
    const userProfile = getUserProfile();
    const isPolicyholder = userProfile === USER_TYPE.policyholder;
    const isCommercial = userProfile === USER_TYPE.commercial;
    let filteredBannerList = [];

    if (banners && banners.length !== 0 && !isCommercial) {
      filteredBannerList = banners.filter(banner => {
        if (isPolicyholder) return banner.onlyPolicyholder && banner;
        if (banner.bonusAvaliableValidate && !hasBonusAvaliable) return null;
        return !banner.onlyPolicyholder && banner;
      });
    }

    return {
      showBanners: !isCommercial && filteredBannerList.length !== 0,
      banners: filteredBannerList
    };
  }, [banners, hasBonusAvaliable]);

  const renderEmptyState = () => (
    <EmptyState
      text={t('processList.emptyState.description')}
      actionLabel={t('processList.emptyState.actionLabel')}
      action={emptyStateAction}
    />
  );
  const renderErrorState = () => (
    <ErrorState
      actionLabel="Tentar novamente"
      text={t('processList.onCrashedError.crashedError')}
      action={errorStateAction}
    />
  );
  const renderEmptySearch = () => (
    <SearchNotFound text={t('processList.search.noResults')} />
  );

  const handleCallbackMenu = (e, redirect) => {
    e.preventDefault();
    const isInvalidBrokerRegister =
      (e.target.id === 'painel-express' ||
        e.target.id === 'cotacao-emissao-Corretor' ||
        e.target.id === 'cotacao-emissao-Comercial' ||
        e.target.id === 'cotacao-emissao-Tomador') &&
      !remoteState.isSusepValidated &&
      remoteState.config.susepCheck;
    if (isInvalidBrokerRegister) {
      callbackMenu('invalidRegister');
    } else {
      window.location = redirect;
    }
  };

  return (
    <>
      <NavigationMenu
        activeId="painel-processos"
        clickMenuCallback={handleCallbackMenu}
      />

      <S.Wrapper>
        <AppBarSearch
          hideFilter={false}
          inputProps={{
            onChange: e => search.onChangeSearchInput(e.target.value),
            onSelect: e => {
              const selectTerm = e.federalId
                ? { policyHolderId: e.value }
                : { status: e.value };
              if (e) search.onChangeSearchInput(e);
              search.onSearch(selectTerm);
            },
            onSelectInputChange: _.debounce(
              e => {
                if (e) search.onChangeSearchInput(e);
                if (e) search.onSelectInputChange(e);
              },
              500,
              { trailing: true }
            ),
            onSearchByDate: e => {
              search.onChangeSearchInput(e);
              search.onSearch({ dateStart: moment(e).format('YYYY-MM-DD') });
            },
            onSearch: () => search.onSearch({ number: search.term }),
            filterChangeCallback: () => {
              search.resetPolicyHoldersList();
              search.onChangeSearchInput('');
            },
            value: search.term,
            isLoadingOptions: search.isFetchingPolicyHolders,
            customOptions: search.policyHoldersList,
            setFilter,
            customMenuItems: menuItems,
            selectStatusOptions,
            selectedStatus,
            setSelectedStatus
          }}
        />

        {filteredBanners.showBanners && (
          <Carousel banners={filteredBanners.banners} />
        )}

        <Announcement {...announcement} />
        <S.ListContainer>
          <S.Title>{t('processList.mainTitle')}</S.Title>
          <S.Subtitle>{t('processList.subtitle')}</S.Subtitle>
          {(showEmptyState && renderEmptyState()) ||
            (showEmptySearch && renderEmptySearch()) ||
            (showErrorState && renderErrorState()) ||
            children}
        </S.ListContainer>

        <ModalOption
          isOpen={modalStatus.open}
          closeModal={callbackMenu}
          {...modalType[modalStatus.type]}
        />

        {pagination.show && !showErrorState && (
          <Pagination
            count={pagination.count}
            page={pagination.pageNumber}
            onChange={pagination.onChange}
            disabled={pagination.disabled}
          />
        )}
      </S.Wrapper>
    </>
  );
}

ProcessesWrapper.defaultProps = {
  hasBonusAvaliable: false
};

ProcessesWrapper.propTypes = {
  children: PropTypes.instanceOf(Object).isRequired,
  showEmptySearch: PropTypes.bool.isRequired,
  showEmptyState: PropTypes.bool.isRequired,
  showErrorState: PropTypes.bool,
  emptyStateAction: PropTypes.func.isRequired,
  errorStateAction: PropTypes.func.isRequired,
  search: PropTypes.shape({
    onChangeSearchInput: PropTypes.func,
    onSearch: PropTypes.func,
    onSelectInputChange: PropTypes.func,
    term: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isFetchingPolicyHolders: PropTypes.bool,
    policyHoldersList: PropTypes.instanceOf(Array),
    resetPolicyHoldersList: PropTypes.func
  }).isRequired,
  pagination: PropTypes.shape({
    count: PropTypes.number,
    pageNumber: PropTypes.number,
    onChange: PropTypes.func,
    disabled: PropTypes.bool
  }).isRequired,
  menuItems: PropTypes.arrayOf(PropTypes.object),
  callbackMenu: PropTypes.func,
  announcement: PropTypes.shape({
    show: PropTypes.bool,
    url: PropTypes.string
  }),
  modalStatus: PropTypes.shape({
    open: PropTypes.bool,
    type: PropTypes.string
  }),
  modalType: PropTypes.object, // eslint-disable-line
  banners: PropTypes.instanceOf(Object),
  hasBonusAvaliable: PropTypes.bool,
  setFilter: PropTypes.number,
  selectStatusOptions: PropTypes.object,
  selectedStatus: PropTypes.object,
  setSelectedStatus: PropTypes.func
};

export default ProcessesWrapper;
